import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import type { Company, PdfTemplateName } from '@/types/database';
import type { WeekStartsOn } from '@/lib/reportPeriod';
import { PdfTemplatePicker } from '@/components/company/PdfTemplatePicker';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { QUOTE_PAYMENT_METHODS, normalizePaymentMethods } from '@/lib/quotePaymentInfo';
import { classifyPixKey } from '@/lib/pixQrCode';
import { PAYMENT_METHOD_LABELS } from '@/lib/financeLabels';
import type { PaymentMethod } from '@/types/database';
import {
  ArrowLeft,
  Building2,
  FileText,
  Mail,
  Phone,
  MapPin,
  Palette,
  Image,
  Upload,
  Loader2,
  Crown,
  QrCode,
  Camera,
  PenLine,
  CalendarDays,
} from 'lucide-react';
import { CONTENT_CARD, FIELD_INPUT, FieldLabel } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { SignaturePad } from '@/components/quotes/SignaturePad';
import { useCamera, dataUrlToFile } from '@/hooks/useCamera';
import { usePhotoUpload } from '@/hooks/usePhotoUpload';
import { useNetworkStatus } from '@/hooks/useNetworkStatus';
import { useAuth } from '@/contexts/AuthContext';
import { addToOfflineQueue } from '@/lib/offlineStorage';
import { cacheCompanies, getCachedCompanies } from '@/lib/offlineDataCache';
import { StorageImage } from '@/components/ui/StorageImage';
import { supabase } from '@/integrations/supabase/client';
import { CompanyFiscalSection, type FiscalSectionValue } from '@/components/company/CompanyFiscalSection';

export default function CompanyFormPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { createCompany, updateCompany, companies } = useCompany();
  const brandColor = useFeature('brand_color');
  const pdfTemplates = useFeature('pdf_templates');
  const documentSignature = useFeature('document_signature');
  const { isOnline } = useNetworkStatus();

  const company: Company | null = id ? companies.find((c) => c.id === id) ?? null : null;
  const isEditing = Boolean(id);

  const [name, setName] = useState('');
  const [cnpjCpf, setCnpjCpf] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  // Assinatura salva do prestador (PRO): guardamos a URL do objeto no Storage,
  // nunca o data URL — a linha de `companies` é cacheada offline.
  const [signatureUrl, setSignatureUrl] = useState('');
  const [signatureOpen, setSignatureOpen] = useState(false);
  const [signatureDraft, setSignatureDraft] = useState<string | null>(null);
  const [savingSignature, setSavingSignature] = useState(false);
  const [primaryColor, setPrimaryColor] = useState('#10B981');
  const [pixKey, setPixKey] = useState('');
  const [pixCity, setPixCity] = useState('');
  const [defaultTerms, setDefaultTerms] = useState('');
  const [defaultPaymentMethods, setDefaultPaymentMethods] = useState<PaymentMethod[]>([]);
  const [weekStartsOn, setWeekStartsOn] = useState<WeekStartsOn>(0);
  // Padrão `true`: é o comportamento anterior à opção. Empresa nova nasce
  // imprimindo a linha, como todas as anteriores.
  const [pdvSignatureLine, setPdvSignatureLine] = useState(true);
  // Padrao `false`: nenhum documento anterior tem marca-d'agua.
  const [logoWatermark, setLogoWatermark] = useState(false);

  /**
   * No nível da EMPRESA, "nenhuma marcada" e "nunca configurei" são a mesma
   * coisa — por isso o estado local é sempre um array e o payload grava `null`
   * quando ele está vazio. A distinção `null` × `[]` que importa é a do
   * DOCUMENTO (ver `src/lib/quotePaymentInfo.ts`): lá, `[]` é a recusa
   * explícita que esconde o QR PIX.
   */
  const toggleDefaultPaymentMethod = (method: PaymentMethod) => {
    setDefaultPaymentMethods((prev) =>
      normalizePaymentMethods(
        prev.includes(method) ? prev.filter((m) => m !== method) : [...prev, method],
      ) ?? [],
    );
  };
  const [pdfTemplate, setPdfTemplate] = useState<PdfTemplateName>('classico');
  const [saving, setSaving] = useState(false);
  const [detectedPixKeyType, setDetectedPixKeyType] = useState<string | null>(null);
  // Dados fiscais (regime + município) reportados pela seção; persistidos no save.
  const [fiscal, setFiscal] = useState<FiscalSectionValue>({ taxpayerType: 'autonomo_informal', muni: null, ready: false });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const { capturePhoto, isCapturing } = useCamera();
  const { uploadFile, uploadPhoto, isUploading: isUploadingLogo } = usePhotoUpload();

  // O rótulo vem de `classifyPixKey`, o MESMO classificador que monta o BR Code
  // — nunca de uma cópia local. Havia uma aqui (e outra no `CompanyFormModal`)
  // que devolvia "Outro" para texto livre: a tela mostrava "✓ Tipo detectado:
  // Outro" e o gerador produzia um QR com chave vazia, que banco nenhum paga.
  const detectPixKeyType = (key: string): string | null => classifyPixKey(key).label;

  // Reset form when the target company changes (mirrors CompanyFormModal's
  // open/company effect).
  useEffect(() => {
    setFiscal({ taxpayerType: 'autonomo_informal', muni: null, ready: false });
    if (company) {
      setName(company.name || '');
      setCnpjCpf(company.cnpj_cpf || '');
      setEmail(company.email || '');
      setPhone(company.phone || '');
      setAddress(company.address || '');
      setLogoUrl(company.logo_url || '');
      setSignatureUrl(company.signature_url || '');
      setPrimaryColor(company.primary_color || '#10B981');
      setPixKey(company.pix_key || '');
      setPixCity(company.pix_city || '');
      setDefaultTerms(company.default_terms || '');
      setDefaultPaymentMethods(normalizePaymentMethods(company.default_payment_methods) ?? []);
      setWeekStartsOn(company.week_starts_on === 1 ? 1 : 0);
      // `!== false` e não `Boolean(...)`: coluna ausente/indefinida (empresa
      // carregada de cache antigo) tem que cair no padrão LIGADO.
      setPdvSignatureLine(company.pdv_signature_line !== false);
      setLogoWatermark(company.logo_watermark === true);
      setPdfTemplate((company.pdf_template as PdfTemplateName) || 'classico');
      setDetectedPixKeyType(detectPixKeyType(company.pix_key || ''));
    } else {
      setName('');
      setCnpjCpf('');
      setEmail('');
      setPhone('');
      setAddress('');
      setLogoUrl('');
      setSignatureUrl('');
      setPrimaryColor('#10B981');
      setPixKey('');
      setPixCity('');
      setDefaultTerms('');
      setDefaultPaymentMethods([]);
      setWeekStartsOn(0);
      setPdfTemplate('classico');
      setDetectedPixKeyType(null);
    }
    setSignatureDraft(null);
    setSignatureOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  const handleCameraUpload = async () => {
    const result = await capturePhoto({
      quality: 85,
      source: 'prompt',
    });

    if (!result?.dataUrl) return;

    if (!isOnline) {
      setLogoUrl(result.dataUrl);
      toast.success('Logo salva localmente! Upload será feito ao sincronizar.', { icon: '📴' });
      return;
    }

    const uploaded = await uploadPhoto(result.dataUrl, 'public-app-assets', 'company-logos');
    if (uploaded) {
      setLogoUrl(uploaded.url);
      toast.success('Logo carregada!');
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.warning('Por favor, selecione uma imagem válida');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.warning('A imagem deve ter no máximo 2MB');
      return;
    }

    if (!isOnline) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setLogoUrl(e.target?.result as string);
        toast.success('Logo salva localmente! Upload será feito ao sincronizar.', { icon: '📴' });
      };
      reader.onerror = () => toast.error('Erro ao carregar a imagem');
      reader.readAsDataURL(file);
      return;
    }

    const uploaded = await uploadFile(file, 'public-app-assets', 'company-logos');
    if (uploaded) {
      setLogoUrl(uploaded.url);
      toast.success('Logo carregada!');
    }
  };

  const handleColorClick = () => {
    if (!brandColor.allowed) brandColor.require();
  };

  const handleOpenSignature = () => {
    if (!documentSignature.allowed) {
      documentSignature.require();
      return;
    }
    if (!isOnline) {
      toast.warning('Sem conexão. Salvar a assinatura requer internet.');
      return;
    }
    setSignatureDraft(null);
    setSignatureOpen(true);
  };

  const handleSaveSignature = async () => {
    if (!signatureDraft) {
      toast.warning('Desenhe a assinatura antes de salvar.');
      return;
    }

    setSavingSignature(true);
    try {
      const file = dataUrlToFile(signatureDraft, 'assinatura.png');
      const uploaded = await uploadFile(file, 'public-app-assets', 'company-signatures');
      if (!uploaded) return;
      setSignatureUrl(uploaded.url);
      setSignatureOpen(false);
      setSignatureDraft(null);
      toast.success('Assinatura pronta! Salve a empresa para aplicá-la nos documentos.');
    } catch (error) {
      console.error('Error uploading signature:', error);
      toast.error('Não foi possível enviar a assinatura. Tente novamente.');
    } finally {
      setSavingSignature(false);
    }
  };

  const handleRemoveSignature = () => {
    if (!documentSignature.allowed) {
      documentSignature.require();
      return;
    }
    setSignatureUrl('');
  };

  const persistFiscalProfile = async (companyId: string) => {
    if (!fiscal.ready) return;
    const cnpjD = cnpjCpf.replace(/\D/g, '');
    const emitter = fiscal.taxpayerType === 'mei' || fiscal.taxpayerType === 'me_epp';
    const { data: existing } = await supabase
      .from('fiscal_profiles').select('id, onboarding_status').eq('company_id', companyId).maybeSingle();

    if (!emitter) {
      if (existing) {
        await supabase.from('fiscal_profiles').update({ taxpayer_type: 'autonomo_informal' }).eq('company_id', companyId);
      }
      return;
    }

    const nextStatus = existing?.onboarding_status === 200 ? 200 : 102;
    const common = {
      taxpayer_type: fiscal.taxpayerType,
      cnpj: cnpjD || null,
      municipio_ibge: fiscal.muni?.ibge ?? null,
      uf: fiscal.muni?.uf ?? null,
      provider: fiscal.muni?.provider ?? 'nacional',
      onboarding_status: nextStatus,
    };
    if (existing) {
      await supabase.from('fiscal_profiles').update(common).eq('company_id', companyId);
    } else {
      await supabase.from('fiscal_profiles').insert({ ...common, company_id: companyId, ambiente: 'homolog' });
    }
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.warning('Nome da empresa é obrigatório');
      return;
    }

    const emitter = fiscal.ready && (fiscal.taxpayerType === 'mei' || fiscal.taxpayerType === 'me_epp');
    if (emitter && cnpjCpf.replace(/\D/g, '').length !== 14) {
      toast.warning('Informe um CNPJ válido (14 dígitos) para o regime selecionado.');
      return;
    }

    setSaving(true);

    const companyData = {
      name: name.trim(),
      cnpj_cpf: cnpjCpf.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      address: address.trim() || null,
      logo_url: logoUrl.trim() || null,
      // O gate PRO está nos handlers e no picker (o estado só muda por eles),
      // então aqui basta PERSISTIR. As três andam juntas de propósito: quem cai
      // para o grátis não tem a assinatura apagada, nem a cor da marca, nem o
      // modelo de PDF — salvar um dado sem relação (o telefone, por exemplo) não
      // pode destruir uma escolha em silêncio. Quem impede o grátis de IMPRIMIR
      // um modelo PRO é o `resolveTemplate` do `pdfGenerator`, na geração.
      signature_url: signatureUrl.trim() || null,
      primary_color: primaryColor,
      pix_key: pixKey.trim() || null,
      pix_city: pixCity.trim() || null,
      default_terms: defaultTerms.trim() || null,
      default_payment_methods: defaultPaymentMethods.length ? defaultPaymentMethods : null,
      week_starts_on: weekStartsOn,
      pdv_signature_line: pdvSignatureLine,
      logo_watermark: logoWatermark,
      pdf_template: pdfTemplate,
    };

    let success = false;
    let savedCompanyId: string | null = null;

    if (isEditing && company) {
      if (!isOnline) {
        toast.warning('Sem conexão. Edição de empresa requer internet.');
        setSaving(false);
        return;
      }
      success = await updateCompany(company.id, companyData);
      if (success) savedCompanyId = company.id;
    } else {
      if (isOnline) {
        const newCompany = await createCompany(companyData);
        success = !!newCompany;
        savedCompanyId = newCompany?.id ?? null;
      } else {
        if (!user) {
          toast.warning('Você precisa estar logado');
          setSaving(false);
          return;
        }

        const tempId = addToOfflineQueue('create_company', { ...companyData, user_id: user.id });

        const offlineCompany: Company = {
          id: tempId,
          user_id: user.id,
          created_at: new Date().toISOString(),
          ...companyData,
          _offline_pending: true,
        } as Company & { _offline_pending: boolean };

        const cachedCompanies = getCachedCompanies(user.id) || [];
        cacheCompanies(user.id, [...cachedCompanies, offlineCompany]);

        window.dispatchEvent(new CustomEvent('company-changed', { detail: { companyId: tempId } }));

        toast.success('Empresa criada offline! Será sincronizada quando houver internet.');
        success = true;
      }
    }

    if (success && savedCompanyId && isOnline) {
      try { await persistFiscalProfile(savedCompanyId); }
      catch { toast.warning('Empresa salva, mas houve um problema ao salvar os dados fiscais.'); }
    }

    setSaving(false);

    if (success) {
      navigate(-1);
    }
  };

  /*
   * HERO — o desenho oficial do app, no molde das telas de CADASTRO
   * (`ClientFormPage` #160, `CatalogItemFormPage` #162):
   *
   *   eyebrow = ONDE você está ("Nova empresa" / "Editando")
   *   h1      = a entidade ("Empresa"), fixo
   *   valor   = o NOME AO VIVO, em largura cheia — a tradução do "total ao
   *             vivo" do orçamento para uma tela cujo conteúdo é identidade.
   *             `line-clamp-2` + `break-words`, nunca `truncate`: razão social
   *             é texto livre e não cabe em uma linha (#160/#175).
   */
  const hero = (
    <div className="relative -mx-4 -mt-6 overflow-hidden rounded-b-[2rem] bg-gradient-to-br from-primary via-primary to-emerald-600 px-4 pb-14 pt-6 text-white shadow-[0_10px_30px_-12px_hsla(var(--primary)/0.6)]">
      <div className="pointer-events-none absolute -right-10 -top-16 h-44 w-44 rounded-full bg-white/10" />
      <div className="pointer-events-none absolute -left-16 top-16 h-40 w-40 rounded-full bg-white/5" />

      <div className="relative flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-9 w-9 shrink-0 rounded-full bg-white/15 text-white hover:bg-white/25 hover:text-white"
          onClick={() => navigate(-1)}
          aria-label="Voltar"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="min-w-0 flex-1">
          <p className="text-[11px] uppercase tracking-wide text-white/70">
            {isEditing ? 'Editando' : 'Nova empresa'}
          </p>
          <h1 className="truncate text-lg font-bold leading-tight">Empresa</h1>
        </div>
      </div>

      <div className="relative mt-5 min-w-0">
        <p className="line-clamp-2 break-words text-2xl font-bold leading-tight">
          {name.trim() || (
            <span className="text-white/60">{isEditing ? 'Carregando…' : 'Nova empresa'}</span>
          )}
        </p>
        <p className="mt-1 truncate text-xs text-white/70">
          {[cnpjCpf.trim() || null, phone.trim() || null].filter(Boolean).join(' · ') ||
            'Preencha os dados abaixo'}
        </p>
      </div>
    </div>
  );

  if (isEditing && !company) {
    return (
      <MainLayout showFab={false} width="form">
        <div className="pb-4">
          {hero}
          <div className="relative z-10 -mt-9 flex justify-center rounded-2xl border border-border/60 bg-card py-16 shadow-sm">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout showFab={false} width="form">
      <div className="pb-4">
        {hero}

        {/* Conteúdo — o primeiro cartão sobe e encosta na curva do hero.
            Os blocos ficam TODOS abertos, e isso é decisão, não descuido: o
            Radix DESMONTA conteúdo de accordion fechado, e a seção fiscal carrega
            o perfil ao montar e o reporta pelo `onChange` — recolhida, ela
            deixaria de reportar e o save passaria a pular `fiscal_profiles` sem
            ninguém pedir. Agrupar em cartões rotulados já resolve a rolagem
            (#162: a régua do accordion é o PAPEL do bloco, não o tamanho). */}
        <div className="relative z-10 -mt-9 space-y-4">
          {/* ── IDENTIDADE ─────────────────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Identidade
            </p>

            {/* Logo */}
            <div className="space-y-1.5">
              <FieldLabel icon={Image}>Logo</FieldLabel>
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg border bg-muted overflow-hidden">
                  {logoUrl ? (
                    <StorageImage
                      src={logoUrl}
                      alt="Logo"
                      className="h-14 w-14 object-cover"
                      fallback={<Image className="h-6 w-6 text-muted-foreground" />}
                    />
                  ) : (
                    <Image className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 flex gap-2">
                  <Input
                    placeholder="https://... ou faça upload"
                    value={logoUrl.startsWith('data:') ? '' : logoUrl}
                    onChange={(e) => setLogoUrl(e.target.value)}
                    className="flex-1"
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="logo-upload"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={handleCameraUpload}
                    disabled={isCapturing || isUploadingLogo}
                    aria-label="Capturar ou escolher imagem"
                  >
                    {isCapturing || isUploadingLogo ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Camera className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploadingLogo}
                    aria-label="Fazer upload de imagem"
                  >
                    {isUploadingLogo ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </div>
              {logoUrl.startsWith('data:') && (
                <p className="text-xs text-muted-foreground">
                  ✓ Imagem carregada do dispositivo
                </p>
              )}
            </div>

            {/* Assinatura do prestador — desenhada uma vez, impressa em todos os
                orçamentos, pedidos e ordens de serviço. Não vale para contrato. */}
            <div className="space-y-1.5">
              <FieldLabel icon={PenLine}>
                Assinatura
                {!documentSignature.allowed && (
                  <span className="ml-1 flex items-center gap-1 text-xs text-amber-600">
                    <Crown className="h-3 w-3" />
                    PRO
                  </span>
                )}
              </FieldLabel>
              <div className="flex items-center gap-3">
                <div className="flex h-14 w-24 shrink-0 items-center justify-center rounded-lg border bg-white overflow-hidden">
                  {signatureUrl ? (
                    <StorageImage
                      src={signatureUrl}
                      alt="Assinatura salva"
                      className="h-14 w-24 object-contain"
                      fallback={<PenLine className="h-6 w-6 text-muted-foreground" />}
                    />
                  ) : (
                    <PenLine className="h-6 w-6 text-muted-foreground" />
                  )}
                </div>
                <div className="flex flex-1 flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleOpenSignature}
                    disabled={isUploadingLogo}
                  >
                    {signatureUrl ? 'Refazer' : 'Assinar'}
                  </Button>
                  {signatureUrl && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={handleRemoveSignature}
                      className="text-muted-foreground hover:text-destructive"
                    >
                      Remover
                    </Button>
                  )}
                </div>
              </div>
              <p className="text-xs text-muted-foreground">
                Aparece acima de "Assinatura do responsável" nos orçamentos, pedidos e
                ordens de serviço. Contratos continuam exigindo assinatura na hora.
              </p>
            </div>

            {/* Name */}
            <div className="space-y-1.5">
              <FieldLabel icon={Building2} htmlFor="company-name">
                Nome da empresa *
              </FieldLabel>
              <Input
                id="company-name"
                placeholder="Minha Empresa LTDA"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className={FIELD_INPUT}
              />
            </div>

            {/* CNPJ/CPF */}
            <div className="space-y-1.5">
              <FieldLabel icon={FileText} htmlFor="company-doc">
                CNPJ / CPF
              </FieldLabel>
              <Input
                id="company-doc"
                placeholder="00.000.000/0000-00"
                value={cnpjCpf}
                onChange={(e) => setCnpjCpf(e.target.value)}
                className={FIELD_INPUT}
              />
            </div>
          </div>

          {/* ── DADOS FISCAIS (NFS-e) ──────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <FileText className="h-3.5 w-3.5 text-primary" />
              Dados fiscais (NFS-e)
            </p>
            <CompanyFiscalSection
              companyId={company?.id ?? null}
              isOwner={true}
              cnpjDigits={cnpjCpf.replace(/\D/g, '')}
              pixCity={pixCity}
              address={address}
              onChange={setFiscal}
            />
          </div>

          {/* ── CONTATO ────────────────────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Contato
            </p>

            {/* Email */}
            <div className="space-y-1.5">
              <FieldLabel icon={Mail} htmlFor="company-email">
                E-mail comercial
              </FieldLabel>
              <Input
                id="company-email"
                type="email"
                placeholder="contato@empresa.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={FIELD_INPUT}
              />
            </div>

            {/* Phone */}
            <div className="space-y-1.5">
              <FieldLabel icon={Phone} htmlFor="company-phone">
                Telefone / WhatsApp
              </FieldLabel>
              <Input
                id="company-phone"
                placeholder="(00) 00000-0000"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={FIELD_INPUT}
              />
            </div>

            {/* Address */}
            <div className="space-y-1.5">
              <FieldLabel icon={MapPin} htmlFor="company-address">
                Endereço
              </FieldLabel>
              <Input
                id="company-address"
                placeholder="Rua Exemplo, 123 - Cidade/UF"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                className={FIELD_INPUT}
              />
            </div>
          </div>

          {/* ── RECEBIMENTO (PIX) ──────────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Recebimento
            </p>

            {/* Pix Key */}
            <div className="space-y-1.5">
              <FieldLabel icon={QrCode} htmlFor="company-pix-key">
                Chave Pix
              </FieldLabel>
              <Input
                id="company-pix-key"
                placeholder="sua.chave@pix.com"
                value={pixKey}
                onChange={(e) => {
                  setPixKey(e.target.value);
                  setDetectedPixKeyType(detectPixKeyType(e.target.value));
                }}
                className={FIELD_INPUT}
              />
              {detectedPixKeyType ? (
                <p className="text-xs text-primary font-medium">
                  ✓ Tipo detectado: {detectedPixKeyType}
                </p>
              ) : pixKey.trim() ? (
                // Aviso, e não erro: nada falhou ainda, e é o usuário quem
                // resolve digitando a chave certa — a fronteira da taxonomia
                // do app (#26). Ele diz o que ACONTECE, porque a consequência
                // (documento sem QR) é invisível até o cliente reclamar.
                // `text-amber-600`, e NÃO `text-warning-foreground`: esse token
                // é `0 0% 100%` (branco puro), feito para ficar SOBRE o âmbar.
                // Solto no cartão, o aviso sairia branco no branco — invisível,
                // e nenhum teste veria (jsdom não tem cor). É o mesmo par que a
                // linha ~548 deste arquivo já usa.
                <p className="text-xs font-medium text-amber-600 dark:text-amber-400">
                  Não reconhecemos esse formato de chave Pix. Use e-mail, celular, CPF, CNPJ ou
                  chave aleatória — senão o orçamento sai sem QR Code.
                </p>
              ) : null}
              <p className="text-xs text-muted-foreground">
                Será exibida no PDF com QR Code para facilitar o pagamento.
              </p>
            </div>

            {/* Pix City */}
            <div className="space-y-1.5">
              <FieldLabel icon={MapPin} htmlFor="company-pix-city">
                Cidade do PIX
              </FieldLabel>
              <Input
                id="company-pix-city"
                placeholder="São Paulo"
                value={pixCity}
                onChange={(e) => setPixCity(e.target.value)}
                className={FIELD_INPUT}
              />
              <p className="text-xs text-muted-foreground">
                Cidade para o QR Code PIX. Se vazio, usa "São Paulo" como padrão.
              </p>
            </div>
          </div>

          {/* ── PADRÕES DOS DOCUMENTOS ─────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Padrões dos documentos
            </p>

            {/* Default Terms */}
            <div className="space-y-1.5">
              <FieldLabel icon={FileText} htmlFor="company-terms">
                Termos e condições padrão
              </FieldLabel>
              <textarea
                id="company-terms"
                placeholder="Ex: O prazo de entrega é de 10 dias úteis após a aprovação..."
                value={defaultTerms}
                onChange={(e) => setDefaultTerms(e.target.value)}
                maxLength={5000}
                className="flex min-h-[100px] w-full rounded-xl border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              />
              <p className="text-xs text-muted-foreground">
                Este texto será preenchido automaticamente ao criar um novo orçamento.
              </p>
            </div>

            {/* Assinatura do cliente no PDV. Fica junto das preferências de
                documento porque governa o que sai impresso, no A4 e no cupom. */}
            <div className="space-y-1.5">
              <div className="flex items-start gap-2">
              {/* Checkbox + div irmã: <label> em volta do Radix alterna 2x (armadilha #29). */}
              <Checkbox
                id="pdv-signature-line"
                checked={pdvSignatureLine}
                onCheckedChange={(v) => setPdvSignatureLine(v === true)}
                onClick={(e) => e.stopPropagation()}
              />
              <div
                className="cursor-pointer text-sm leading-tight"
                onClick={() => setPdvSignatureLine(!pdvSignatureLine)}
              >
                Linha de assinatura do cliente na venda rápida
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Desmarque se a venda de balcão já sai paga e você não pede assinatura.
              Vale para o PDF e para o cupom térmico. A linha do responsável continua.
            </p>
          </div>

            {/* Marca-d'água da logo. Fica junto da assinatura do PDV: as duas
                governam o que sai impresso no documento. */}
            <div className="space-y-1.5">
              <div className="flex items-start gap-2">
              <Checkbox
                id="logo-watermark"
                checked={logoWatermark}
                onCheckedChange={(v) => setLogoWatermark(v === true)}
                onClick={(e) => e.stopPropagation()}
              />
              <div
                className="cursor-pointer text-sm leading-tight"
                onClick={() => setLogoWatermark(!logoWatermark)}
              >
                Logo como marca-d'água no PDF
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Imprime a logo bem clara atrás do conteúdo. Precisa de logo cadastrada
              e não vale para o cupom térmico, que imprime só em preto e branco.
            </p>
          </div>

            {/* Formas de pagamento aceitas (padrão) */}
            <div className="space-y-1.5">
              <FieldLabel icon={FileText}>Formas de pagamento aceitas (padrão)</FieldLabel>
            <div className="grid grid-cols-2 gap-x-3 gap-y-2">
              {QUOTE_PAYMENT_METHODS.map((method) => {
                const checked = defaultPaymentMethods.includes(method);
                return (
                  <div key={method} className="flex items-center gap-2">
                    {/* Checkbox + div irmã: <label> em volta do Radix alterna 2x (armadilha #29). */}
                    <Checkbox
                      id={`default-payment-${method}`}
                      checked={checked}
                      onCheckedChange={() => toggleDefaultPaymentMethod(method)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div
                      className="cursor-pointer text-sm leading-tight"
                      onClick={() => toggleDefaultPaymentMethod(method)}
                    >
                      {PAYMENT_METHOD_LABELS[method]}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground">
              Vem pré-selecionado em cada orçamento novo, e continua editável lá. Sem
              nenhuma marcada, um orçamento novo só marca PIX se você tiver chave
              cadastrada acima.
            </p>
          </div>

            {/* PDF Template - with upsell for non-PRO */}
            <div className="space-y-1.5">
              <FieldLabel icon={FileText}>
                Modelo do PDF
                {!pdfTemplates.allowed && (
                  <span className="ml-1 flex items-center gap-1 text-xs text-amber-600">
                    <Crown className="h-3 w-3" />
                    PRO
                  </span>
                )}
              </FieldLabel>
            <PdfTemplatePicker
              value={pdfTemplate}
              onChange={setPdfTemplate}
              accent={primaryColor}
            />
            <p className="text-xs text-muted-foreground">
              É o modelo PADRÃO: todo orçamento novo nasce com ele, e dá para
              trocar em cada orçamento na própria tela.
            </p>
          </div>

            {/* Primary Color - with upsell for non-PRO */}
            <div className="space-y-1.5">
              <FieldLabel icon={Palette}>
                Cor principal
                {!brandColor.allowed && (
                  <span className="ml-1 flex items-center gap-1 text-xs text-amber-600">
                    <Crown className="h-3 w-3" />
                    PRO
                  </span>
                )}
              </FieldLabel>
            <div className="flex items-center gap-3">
              <div className="relative" onClick={handleColorClick}>
                <input
                  type="color"
                  value={primaryColor}
                  onChange={(e) => {
                    if (brandColor.allowed) {
                      setPrimaryColor(e.target.value.toUpperCase());
                    }
                  }}
                  onClick={(e) => {
                    if (!brandColor.allowed) {
                      e.preventDefault();
                      handleColorClick();
                    }
                  }}
                  className={`h-10 w-16 rounded-lg border cursor-pointer ${!brandColor.allowed ? 'cursor-pointer' : ''}`}
                  style={{ pointerEvents: brandColor.allowed ? 'auto' : 'none' }}
                />
                {!brandColor.allowed && (
                  <div
                    className="absolute inset-0 cursor-pointer"
                    onClick={handleColorClick}
                  />
                )}
              </div>
              <Input
                value={primaryColor}
                onChange={(e) => {
                  if (!brandColor.allowed) {
                    handleColorClick();
                    return;
                  }
                  let value = e.target.value.toUpperCase();
                  if (value && !value.startsWith('#')) {
                    value = '#' + value;
                  }
                  if (value.length <= 7) {
                    setPrimaryColor(value);
                  }
                }}
                onClick={() => {
                  if (!brandColor.allowed) handleColorClick();
                }}
                  placeholder="#10B981"
                  className={`w-24 font-mono text-sm cursor-pointer`}
                />
              </div>
            </div>
          </div>

          {/* ── PREFERÊNCIAS ───────────────────────────────────────────── */}
          <div className={cn(CONTENT_CARD, 'space-y-3 p-4')}>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Preferências
            </p>

            <div className="space-y-1.5">
              <FieldLabel icon={CalendarDays} htmlFor="week-start-page">
                Início da semana
              </FieldLabel>
              <select
                id="week-start-page"
                value={weekStartsOn}
                onChange={(event) => setWeekStartsOn(event.target.value === '1' ? 1 : 0)}
                className="h-10 w-full rounded-xl border border-input bg-background px-3 text-sm"
              >
                <option value={0}>Domingo</option>
                <option value={1}>Segunda-feira</option>
              </select>
              <p className="text-xs text-muted-foreground">
                Vale igualmente para “Esta semana” nos relatórios, Agenda e Dashboard.
              </p>
            </div>
          </div>
        </div>

        {/*
          Barra FLUTUANTE acima da navegação inferior — a mesma das telas de
          orçamento e de cliente. Precisa ser filha DIRETA do container da
          página: `sticky` só desliza dentro do próprio pai.

          Os dois botões continuam aqui (Cancelar é a saída explícita, além do
          voltar do hero), e o rótulo do primário continua sendo "Salvar" na
          edição e "Criar empresa" na criação.
        */}
        <div className="sticky bottom-[calc(var(--bottom-nav-h)+0.5rem)] z-20 mt-6 flex gap-2 rounded-2xl border border-border/60 bg-card/95 p-2 shadow-[0_-6px_20px_-10px_rgba(0,0,0,0.35)] backdrop-blur">
          <Button3D color="gray" size="sm" onClick={() => navigate(-1)} className="shrink-0">
            Cancelar
          </Button3D>
          <Button3D size="sm" onClick={handleSubmit} disabled={saving} className="flex-1">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Salvando...
              </>
            ) : isEditing ? (
              'Salvar'
            ) : (
              'Criar empresa'
            )}
          </Button3D>
        </div>
      </div>

      {/* Quadro de assinatura do prestador. O upload acontece aqui; a coluna
          `signature_url` só é gravada quando a empresa for salva. */}
      <Dialog open={signatureOpen} onOpenChange={setSignatureOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <PenLine className="h-5 w-5 text-primary" />
              Sua assinatura
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <SignaturePad label="Assine no quadro abaixo" onSignatureChange={setSignatureDraft} />
            <p className="text-xs text-muted-foreground">
              Ela será impressa acima da linha "Assinatura do responsável". Você pode
              refazê-la quando quiser.
            </p>
          </div>
          <DialogFooter>
            <Button3D color="gray" onClick={() => setSignatureOpen(false)} disabled={savingSignature}>
              Cancelar
            </Button3D>
            <Button3D onClick={handleSaveSignature} disabled={savingSignature || !signatureDraft}>
              {savingSignature ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Usar esta assinatura'
              )}
            </Button3D>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </MainLayout>
  );
}
